import React, { useContext, useRef, useState } from "react";
import { AutoComplete, Button, Spin } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { ENDPOINT } from "../config";
import { AuthenticatedApiContext } from "../../context/AuthenticatedApiContext";

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;

export function TemplateSearchField({ form }) {
  const { get } = useContext(AuthenticatedApiContext);
  const [searchValue, setSearchValue] = useState("");
  const [options, setOptions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const debounceRef = useRef(null);
  const latestTermRef = useRef("");

  const runSearch = async (term) => {
    try {
      const results = await get(
        `${ENDPOINT}/search-processes/${encodeURIComponent(term)}`
      );
      if (latestTermRef.current !== term) return;
      setOptions(
        (results || []).map((process) => ({
          value: process.company_id,
          company_name: process.company_name,
          company_id: process.company_id,
          label: (
            <div className="md-option">
              <span className="md-option-name">{process.company_name}</span>
              <span className="md-option-id">{process.company_id}</span>
            </div>
          ),
        }))
      );
    } catch (error) {
      if (latestTermRef.current !== term) return;
      setOptions([]);
    } finally {
      if (latestTermRef.current === term) setIsSearching(false);
    }
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    form.setFieldsValue({ "config.templateCompanyId": undefined });

    clearTimeout(debounceRef.current);
    const term = value.trim();
    latestTermRef.current = term;
    if (term.length < MIN_SEARCH_LENGTH) {
      setIsSearching(false);
      setOptions([]);
      return;
    }
    setIsSearching(true);
    setOptions([]);
    debounceRef.current = setTimeout(() => runSearch(term), SEARCH_DEBOUNCE_MS);
  };

  const handleSelect = (companyId, option) => {
    form.setFieldsValue({ "config.templateCompanyId": companyId });
    setSelected({
      company_id: option.company_id,
      company_name: option.company_name,
    });
    setSearchValue("");
    setOptions([]);
  };

  const handleClear = () => {
    form.setFieldsValue({ "config.templateCompanyId": undefined });
    setSelected(null);
    setSearchValue("");
    setOptions([]);
  };

  if (selected) {
    return (
      <div className="md-chip">
        <span className="md-chip-text">
          <span className="md-chip-name">{selected.company_name}</span>
          <span className="md-chip-id">{selected.company_id}</span>
        </span>
        <Button
          type="text"
          size="small"
          shape="circle"
          icon={<CloseOutlined />}
          onClick={handleClear}
          aria-label="Remove selected template"
        />
      </div>
    );
  }

  return (
    <AutoComplete
      value={searchValue}
      options={options}
      onSearch={handleSearch}
      onSelect={handleSelect}
      onChange={setSearchValue}
      notFoundContent={
        isSearching ? (
          <Spin size="small" />
        ) : searchValue.trim().length >= MIN_SEARCH_LENGTH ? (
          "No companies found"
        ) : null
      }
      placeholder="Search by company name or company ID..."
      popupClassName="md-dropdown"
      style={{ width: "100%" }}
    />
  );
}
